/**
 * Escribir una entrada del blog.
 *
 * Una hoja, no un formulario: la portada, el título grande y el cuerpo con el
 * mismo editor de bloques que usa el CMS en el editor (`BlockEditor` de
 * `@webbuilder/ui`). Lo que no es escribir —la dirección, el extracto, las
 * categorías, la fecha y lo que ven los buscadores— va en la columna de al
 * lado, donde se mira de reojo y no estorba.
 *
 * Guardar y descartar son la barra común de arriba a la derecha
 * (`PendingChangesActions`), que además avisa si se intenta salir a medias.
 * Publicar es un botón aparte y deliberado: guardar una publicada la actualiza
 * en el sitio, pero un borrador no sale hasta que se pide.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { IconUser, IconTag, IconHeading, IconAlignLeft, IconArrowLeft, IconCheck, IconCopy, IconExternalLink, IconFileText, IconPencil, IconPhoto, IconPhotoPlus, IconSearch, IconSend, IconShare, IconTrash, IconWorld, IconX } from '@tabler/icons-react'
import { AutoSizeTextarea, RecordHeader, formatRecordDateTime, RecordProperty, RecordImageAdd, RecordImagePreview, BlockEditor, BlockEditorEnvProvider, type BlockEditorEnv, VelticaMascot } from '@webbuilder/ui'
import { BLOG_LIMITS, blogAuthorNames, blogAuthorValue, blogPostState, blogSlug, type BlogPost } from '@webbuilder/schemas'
import { DEFAULT_PAGE_THEME, normalizeProjectTheme } from '@webbuilder/theme'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { InfoHint } from '../../SettingsControls'
import { useTabThumb } from '../../../hooks/useTabThumb'
import { SingleDatePicker } from '../DateRangePicker'
import { BlogAuthorField, BlogCategoryField, type KnownOption } from './BlogPickerField'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { ConfirmDialog } from '../ConfirmDialog'
import { useToast } from '../../Toast'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { formatDashboardListDate, formatDashboardListDateWithTime } from '../../../lib/dashboardListDate'
import { createBlogPost, deleteBlogPost, getBlogPost, getProjectTheme, serverErrorCode, updateBlogPost, type BlogPostInput } from '../../../lib/api'
import { richContentCss } from '../../../lib/preview/render'
import { errorMessage } from './offerShared'

export type Draft = {
  title: string
  slug: string
  excerpt: string
  coverImage: string
  coverAlt: string
  content: string
  authorName: string
  categories: string[]
  publishedAt: string
  seoTitle: string
  seoDescription: string
  seoImage: string
}

/** Dónde se lee una entrada en el sitio publicado. */
/**
 * Los colores de un tema como variables `--wb-*`, las que lee el contenido del
 * cuerpo (`richContentCss`): `--wb-text` para la línea de la cita, `--wb-primary`
 * para el fondo del destacado…
 */
export function siteColorVars(colors: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(colors).map(([key, value]) => [`--wb-${key}`, value]))
}

export function blogPostUrl(siteOrigin: string, basePath: string, slug: string): string {
  return `${siteOrigin.replace(/\/$/, '')}/${basePath}/${slug}`
}

/** Lo que Google corta: el recuento avisa, no impide. Los mismos que en la página de una oferta. */
const SEO_TITLE_CHARS = 60
const SEO_DESCRIPTION_CHARS = 160

export const EMPTY: Draft = {
  title: '', slug: '', excerpt: '', coverImage: '', coverAlt: '', content: '', authorName: '',
  categories: [], publishedAt: '', seoTitle: '', seoDescription: '', seoImage: '',
}

/** La fecha como la pide un `datetime-local`, en la hora de quien escribe. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * El HTML tal como lo escribe el navegador. El que guarda el servidor está
 * limpio pero no es idéntico al que devuelve el editor al salir de él —el
 * navegador escribe `allowfullscreen=""`, reordena espacios…—, y sin igualarlos
 * salir del cuerpo sin tocar nada encendía la barra de guardar.
 */
function browserHtml(html: string): string {
  if (typeof document === 'undefined') return html
  // Un `template` es inerte: no descarga las imágenes que se le meten.
  const holder = document.createElement('template')
  holder.innerHTML = html
  return holder.innerHTML
}

function draftOf(post: BlogPost | null): Draft {
  if (!post) return EMPTY
  return {
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt,
    coverImage: post.coverImage,
    coverAlt: post.coverAlt,
    content: browserHtml(post.content),
    authorName: post.authorName,
    categories: post.categories,
    publishedAt: toLocalInput(post.publishedAt),
    seoTitle: post.seoTitle,
    seoDescription: post.seoDescription,
    seoImage: post.seoImage,
  }
}

/** Sólo lo que cambió: una escritura que no toca el slug no puede chocar con otro. */
export function changes(base: Draft, next: Draft): BlogPostInput {
  const input: BlogPostInput = {}
  for (const key of Object.keys(next) as (keyof Draft)[]) {
    if (JSON.stringify(base[key]) === JSON.stringify(next[key])) continue
    if (key === 'publishedAt') input.publishedAt = next.publishedAt ? new Date(next.publishedAt).toISOString() : null
    else if (key === 'categories') input.categories = next.categories
    else input[key] = next[key]
  }
  return input
}

/**
 * Los campos del blog usan la misma medición y el mismo recuadro que el CMS.
 */
function AutoGrowText({ className, value, maxLength, placeholder, onChange }: {
  className: string
  value: string
  maxLength: number
  placeholder: string
  onChange: (value: string) => void
}) {
  return (
    <AutoSizeTextarea
      className={className}
      rows={1}
      value={value}
      maxLength={maxLength}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/**
 * El editor de bloques en el panel: los textos del panel, el aspecto del
 * contenido y el selector de imágenes del panel.
 */
export function useBlockEditorEnv(token: string, projectId: string): BlockEditorEnv {
  const { t } = useI18n()
  const language = activeLanguage()
  return useMemo(() => ({
    t: t as BlockEditorEnv['t'],
    language,
    contentCss: richContentCss(undefined, '.cms-blocks-editor'),
    renderImagePicker: ({ onSelect, onClose }): ReactNode => (
      <ImageLibraryPickerDialog token={token} projectId={projectId} onSelect={onSelect} onClose={onClose} />
    ),
  }), [t, language, token, projectId])
}

export function BlogPostEditor({ projectId, token, postId, basePath, siteOrigin, knownCategories, knownAuthors, onCategoriesChanged, onBack, onCreated, onDeleted, onLimitReached }: {
  projectId: string
  token: string
  /** Las categorías y los autores que ya usa el blog, para sugerirlos. */
  knownCategories: KnownOption[]
  knownAuthors: KnownOption[]
  /** Se eliminó una categoría o un autor de todo el blog: el listado tiene que volver a leerse. */
  onCategoriesChanged: () => void
  /** `null`: una entrada nueva, que existe al guardarla por primera vez. */
  postId: string | null
  basePath: string
  /** El dominio del sitio publicado; sin él la entrada aún no tiene enlace. */
  siteOrigin: string | null
  onBack: () => void
  onCreated: (post: BlogPost) => void
  onDeleted: () => void
  /** Publicar chocó con el tope de entradas del plan: abre la ventana de mejorar plan. */
  onLimitReached: () => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const env = useBlockEditorEnv(token, projectId)

  /**
   * Los colores del sitio, declarados como variables `--wb-*` en el cuerpo.
   *
   * Lo que el editor toma del tema es la FORMA de los bloques: la línea de la
   * cita, el fondo del destacado, el separador, el bloque de código. Todo eso
   * está escrito con `var(--wb-text)` y `var(--wb-primary)`, que sólo existen
   * en la página publicada. En el panel no estaban definidas, y una `var()` sin
   * definir invalida la declaración entera: la cita salía sin línea, el
   * destacado sin fondo y el separador sin trazo. Con las del sitio, el
   * editor se ve como se publica. Hasta que llega el tema —o si falla— valen
   * las de fábrica, que tampoco dejan nada sin pintar.
   */
  const [siteTheme, setSiteTheme] = useState(() => normalizeProjectTheme(null, DEFAULT_PAGE_THEME))
  useEffect(() => {
    let active = true
    getProjectTheme(projectId, token)
      .then((row) => { if (active) setSiteTheme(normalizeProjectTheme(row.theme, DEFAULT_PAGE_THEME)) })
      .catch(() => { /* sin tema, el de fábrica */ })
    return () => { active = false }
  }, [projectId, token])
  const siteVars = useMemo(() => siteColorVars(siteTheme.colors) as CSSProperties, [siteTheme])

  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(postId !== null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [base, setBase] = useState<Draft>(EMPTY)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  /* El cuerpo no pasa por `draft` mientras se escribe: el editor de bloques lo
     entrega al salir de él. Esto dice que hay algo a medias, para que la barra
     de guardar aparezca desde la primera tecla; y la referencia guarda lo último
     entregado, que el clic en Guardar recibe antes de su propio evento. */
  const [typing, setTyping] = useState(false)
  const contentRef = useRef('')
  /** Remonta el editor de bloques al descartar: no es controlado. */
  const [bodyKey, setBodyKey] = useState(0)
  /** En una nueva, la dirección sigue al título hasta que alguien la toca. */
  const [slugTouched, setSlugTouched] = useState(postId !== null)
  const [saving, setSaving] = useState(false)
  const [attention, setAttention] = useState(0)
  const [picking, setPicking] = useState<null | 'cover' | 'seo'>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [copied, setCopied] = useState(false)
  const loader = useRecordLoader(loading)
  /* Escribir y lo que ven los buscadores, por separado: son dos momentos
     distintos y juntos la columna de al lado se hacía interminable. */
  const [tab, setTab] = useState<'write' | 'seo'>('write')
  const tabsRef = useRef<HTMLDivElement | null>(null)
  /* Mientras carga no hay barra que medir: la clave cambia al aparecer. */
  useTabThumb(tabsRef, loader.visible ? 'loading' : tab)

  useEffect(() => {
    if (postId === null) return
    let alive = true
    setLoading(true)
    getBlogPost(projectId, postId, token)
      .then((loaded) => {
        if (!alive) return
        const next = draftOf(loaded)
        setPost(loaded)
        setBase(next)
        setDraft(next)
        contentRef.current = next.content
        setBodyKey((key) => key + 1)
      })
      .catch(() => { if (alive) setLoadFailed(true) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [postId, projectId, token])

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((current) => ({ ...current, [key]: value }))
  const authorRemovedEverywhere = (name: string) => {
    const without = (current: Draft) => ({ ...current, authorName: blogAuthorValue(blogAuthorNames(current.authorName).filter((author) => author !== name)) })
    setBase(without)
    setDraft(without)
    onCategoriesChanged()
  }

  const pending = changes(base, { ...draft, content: contentRef.current })
  const dirty = typing || Object.keys(pending).length > 0 || (postId === null && (draft.title.trim() !== '' || typing))

  const save = useCallback(async (status?: 'draft' | 'published') => {
    const next = { ...draft, content: contentRef.current }
    const input: BlogPostInput = { ...changes(base, next), ...(status ? { status } : {}) }
    /* La dirección de una nueva: la escrita, o la que sale del título. En una
       que ya existe sólo se manda si se cambió, para no chocar sin motivo. */
    if (postId === null && !input.slug) input.slug = next.slug || blogSlug(next.title)
    setSaving(true)
    try {
      const saved = postId === null
        ? await createBlogPost(projectId, input, token)
        : await updateBlogPost(projectId, postId, input, token)
      const fresh = draftOf(saved)
      setPost(saved)
      setBase(fresh)
      setDraft(fresh)
      contentRef.current = fresh.content
      setTyping(false)
      setSlugTouched(true)
      if (status === 'published') {
        showToast(t(saved.state === 'scheduled' ? 'blog.toast.scheduled' : 'blog.toast.published', {
          date: saved.publishedAt ? formatDashboardListDate(saved.publishedAt) : '',
        }))
      } else if (status === 'draft') {
        showToast(t('blog.toast.draft'))
      } else {
        showToast(t('blog.toast.saved'))
      }
      if (postId === null) onCreated(saved)
    } catch (error) {
      /* El tope no es un fallo que se lea en un aviso: tiene su ventana, la
         misma que las ofertas y las páginas, con la salida de subir de plan. */
      if (serverErrorCode(error) === 'blog.publishLimitReached') onLimitReached()
      else showToast(errorMessage(error), 'error')
    } finally {
      setSaving(false)
    }
  }, [base, draft, onCreated, onLimitReached, postId, projectId, showToast, t, token])

  const discard = () => {
    setDraft(base)
    contentRef.current = base.content
    setTyping(false)
    setBodyKey((key) => key + 1)
  }

  if (loader.visible) return <SectionLoader loader={loader} label={t('blog.loading')} />
  if (loadFailed) {
    return (
      <div className="dash-projects-view dash-pages-view dash-blog-editor-view">
        <button type="button" className="dash-secondary-btn dash-blog-back" onClick={onBack}>
          <IconArrowLeft size={15} /> {t('blog.back')}
        </button>
        <div className="dash-inline-msg"><span>{t('blog.editor.loadFailed')}</span></div>
      </div>
    )
  }

  /* El enlace es el de lo GUARDADO: la dirección escrita y sin guardar todavía
     no existe en el sitio. */
  const publicUrl = siteOrigin && post ? blogPostUrl(siteOrigin, basePath, post.slug) : null
  /* Con enlace pero sin publicar: el enlace ya se puede copiar, pero en el
     sitio contesta «no existe» hasta publicarla (o hasta su fecha). Sin este
     aviso, faltaba el botón «Abrir» y no se decía por qué. */
  const liveHint = publicUrl && post?.state !== 'published'
    ? t(post?.state === 'scheduled' ? 'blog.link.liveScheduled' : 'blog.link.liveAfterPublish')
    : ''
  const copyLink = async () => {
    if (!publicUrl) return
    try {
      await navigator.clipboard.writeText(publicUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      showToast(t('blog.link.copyFailed'), 'error')
    }
  }
  const seoTitle = draft.seoTitle.trim() || draft.title.trim() || t('blog.untitled')
  const seoDescription = draft.seoDescription.trim() || draft.excerpt.trim()
  const shareImage = draft.seoImage || draft.coverImage
  const state = post ? blogPostState({ status: post.status, publishedAt: post.publishedAt }) : 'draft'
  const futureDate = draft.publishedAt && new Date(draft.publishedAt).getTime() > Date.now()
  const publishedAt = state === 'published' ? post?.publishedAt : null
  const publicationLabel = publishedAt
    ? t('blog.date.published', { date: formatDashboardListDateWithTime(publishedAt) })
    : null

  return (
    <div className="dash-projects-view dash-pages-view dash-blog-editor-view dash-loaded-content-in">
      <PendingChangesActions
        dirty={dirty}
        saving={saving}
        attentionKey={attention}
        onDiscard={discard}
        onSave={() => { void save() }}
      />

      {/* La cabecera de las fichas —la de un producto digital—: qué es y en qué
          estado está a la izquierda; volver a la derecha. Publicar va en la
          tarjeta de la entrada, como «Ajustes» en la de una oferta. */}
      <DashboardPageHeader
        title={t('blog.editor.kind')}
        actions={(
          <>
            <button
              type="button"
              className="dash-secondary-btn dash-detail-back"
              onClick={() => { if (dirty) { setAttention((n) => n + 1); return } onBack() }}
            >
              <IconArrowLeft size={14} /> {t('blog.back')}
            </button>
          </>
        )}
      />

      <div
        className="dash-booking-service-tabs dash-tabs-icon-collapse dash-blog-tabs"
        ref={tabsRef}
        role="tablist"
        aria-label={t('blog.tab.aria')}
      >
        <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
        {([
          ['write', 'blog.tab.write', IconPencil],
          ['seo', 'blog.seo.title', IconSearch],
        ] as const).map(([value, labelKey, Icon]) => (
          <button
            key={value}
            id={`blog-tab-${value}`}
            type="button"
            role="tab"
            aria-selected={tab === value}
            aria-controls={`blog-panel-${value}`}
            tabIndex={tab === value ? 0 : -1}
            className={tab === value ? 'is-active' : ''}
            onClick={() => setTab(value)}
          >
            <Icon size={15} />
            <span>{t(labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Las dos pestañas quedan montadas y sólo se oculta la otra: el editor de
          bloques entrega el cuerpo al perder el foco, y desmontarlo a media
          frase se llevaría lo último escrito. */}
      {/* A la izquierda, lo de cada pestaña; a la derecha, la entrada —su
          tarjeta, igual que la de un producto digital— en las dos. */}
      <div className="dash-blog-editor-layout">
      <div className="dash-blog-main">
      <div id="blog-panel-write" role="tabpanel" aria-labelledby="blog-tab-write" hidden={tab !== 'write'}>
        {/* Propiedades con los campos compartidos del CMS y el mismo editor de contenido. */}
        <article className="dash-blog-sheet">
          <div className="dash-blog-column">
            <RecordHeader
              flush
              title={draft.title.trim() || t('blog.untitled')}
              modifiedLabel={t('cms.modified')}
              modifiedDate={formatRecordDateTime(post?.updatedAt, activeLanguage())}
              createdLabel={t('cms.created')}
              createdDate={formatRecordDateTime(post?.createdAt, activeLanguage())}
            />
            {post?.setupPending ? (
              /* La empezó la IA: título y resumen suyos, el cuerpo de relleno.
                 El servidor no la deja publicar hasta que se escriba. */
              <p className="dash-ai-pending-note">
                <VelticaMascot outline size={17} />
                <span>{t('aiPending.postNote')}</span>
              </p>
            ) : null}

            <div className="record-fields record-fields--flush dash-blog-properties">
              <RecordProperty label={t('common.title')} icon={<IconHeading size={14} />}>
                <AutoGrowText
                  className="dash-blog-title record-input"
                  value={draft.title}
                  maxLength={BLOG_LIMITS.title}
                  placeholder={t('blog.editor.titlePlaceholder')}
                  onChange={(value) => {
                    const title = value.replace(/\n/g, ' ')
                    setDraft((current) => ({ ...current, title, ...(slugTouched ? {} : { slug: title.trim() ? blogSlug(title) : '' }) }))
                  }}
                />
              </RecordProperty>
              <RecordProperty label={t('blog.field.excerpt')} icon={<IconAlignLeft size={14} />}>
                <AutoGrowText
                  className="dash-blog-dek record-input"
                  value={draft.excerpt}
                  maxLength={BLOG_LIMITS.excerpt}
                  placeholder={t('blog.editor.excerptPlaceholder')}
                  onChange={(value) => set('excerpt', value.replace(/\n/g, ' '))}
                />
              </RecordProperty>
              <RecordProperty label={t('gallery.cover')} icon={<IconPhoto size={14} />}>
                <div className="cms-gallery">
                  <ul className="cms-gallery-grid is-fixed">
                    {draft.coverImage ? (
                      <li className="cms-gallery-tile">
                        <RecordImagePreview
                          src={draft.coverImage}
                          alt={draft.coverAlt}
                          removeLabel={t('blog.editor.removeCover')}
                          onRemove={() => set('coverImage', '')}
                          onOpen={() => setPicking('cover')}
                          openLabel={t('blog.editor.changeCover')}
                        />
                      </li>
                    ) : (
                      <RecordImageAdd
                        label={t('blog.editor.addCover')}
                        ariaLabel={t('blog.editor.addCover')}
                        onClick={() => setPicking('cover')}
                      />
                    )}
                  </ul>
                </div>
              </RecordProperty>
              <RecordProperty label={t('blog.field.author')} icon={<IconUser size={14} />}>
                <BlogAuthorField
                  hideLabel
                  projectId={projectId}
                  token={token}
                  value={draft.authorName}
                  known={knownAuthors}
                  onChange={(authorName) => set('authorName', authorName)}
                  onRemovedEverywhere={authorRemovedEverywhere}
                />
              </RecordProperty>
              <RecordProperty label={t('blog.field.categories')} icon={<IconTag size={14} />}>
                <BlogCategoryField
                  hideLabel
                  projectId={projectId}
                  token={token}
                  value={draft.categories}
                  known={knownCategories}
                  onChange={(categories) => set('categories', categories)}
                  onRemovedEverywhere={(name) => {
                    /* El servidor ya se la quitó a esta entrada si la tenía guardada:
                       se quita de lo guardado y de lo escrito, para que no parezca
                       un cambio pendiente. */
                    const without = (current: Draft) => ({ ...current, categories: current.categories.filter((item) => item !== name) })
                    setBase(without)
                    setDraft(without)
                    onCategoriesChanged()
                  }}
                />
              </RecordProperty>
              {/* El cuerpo, como una propiedad más pero a todo el ancho y en una
                  caja de casilla: así se ve que ahí va el contenido. Fijo al
                  final: en el blog no se reordena ni se agregan propiedades. */}
              <RecordProperty className="record-property--long" label={t('cms.type.content')} icon={<IconFileText size={14} />}>
                <div className="record-long-box dash-block-editor-host" style={siteVars}>
                  <BlockEditorEnvProvider value={env}>
                    <BlockEditor
                      key={bodyKey}
                      idleAdd
                      value={draft.content}
                      onCommit={(html) => { contentRef.current = html; setDraft((current) => ({ ...current, content: html })); setTyping(false) }}
                      onDraft={(hasDraft) => { if (hasDraft) setTyping(true) }}
                    />
                  </BlockEditorEnvProvider>
                </div>
              </RecordProperty>
            </div>
          </div>
        </article>

      </div>

      <div id="blog-panel-seo" role="tabpanel" aria-labelledby="blog-tab-seo" hidden={tab !== 'seo'} className="dash-project-general-content dash-blog-seo-panel">
        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
              <IconWorld size={18} />
            </span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('blog.field.slug')}</h3>
              <p>{t(post?.status === 'published' ? 'blog.field.slugHint' : 'blog.address.body')}</p>
            </div>
          </div>
          <label className="dash-field">
            <span>{t('pageSettings.slug')}</span>
            <div className="dash-page-settings-url">
              <div className="dash-page-settings-url-box">
                <small title={`${siteOrigin ?? ''}/${basePath}/`}>{siteOrigin ? siteOrigin.replace(/^https?:\/\//, '') : ''}/{basePath}/</small>
                <input
                  value={draft.slug}
                  maxLength={BLOG_LIMITS.slug}
                  placeholder={blogSlug(draft.title)}
                  aria-label={t('blog.field.slug')}
                  onChange={(event) => { setSlugTouched(true); set('slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')) }}
                  onBlur={() => { if (draft.slug) set('slug', blogSlug(draft.slug)) }}
                />
              </div>
              <button type="button" className="dash-secondary-btn" disabled={!publicUrl} onClick={() => { void copyLink() }}>
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                <span>{t(copied ? 'blog.link.copied' : 'blog.link.copy')}</span>
              </button>
              {publicUrl && post?.state === 'published' ? (
                <a className="dash-secondary-btn" href={publicUrl} target="_blank" rel="noreferrer">
                  <IconExternalLink size={14} />
                  <span>{t('blog.link.open')}</span>
                </a>
              ) : null}
            </div>
          </label>
          {siteOrigin ? null : <small className="dash-page-settings-hint">{t('blog.link.needSite')}</small>}
          {liveHint ? <small className="dash-page-settings-hint">{liveHint}</small> : null}
        </section>

        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
              <IconSearch size={18} />
            </span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('blog.seo.searchTitle')}</h3>
              <p>{t('blog.seo.searchBody')}</p>
            </div>
          </div>
          {/* Lo que se escribe abajo, tal como sale en un buscador: sin verlo,
              «título» y «descripción» son dos cajas más. */}
          <div className="dash-blog-serp" aria-label={t('blog.seo.preview')}>
            <span className="dash-blog-serp-label">{t('blog.seo.preview')}</span>
            <span className="dash-blog-serp-url">/{basePath}/{draft.slug || blogSlug(draft.title) || '…'}</span>
            <strong className="dash-blog-serp-title">{seoTitle}</strong>
            {seoDescription ? <p>{seoDescription}</p> : <p className="is-empty">{t('blog.seo.previewEmpty')}</p>}
          </div>
          <label className="dash-field">
            <span>{t('pageSettings.seoTitle')} <InfoHint text={t('pageSettings.seoTitleHint')} /></span>
            <input
              className="dash-placeholder-strong"
              value={draft.seoTitle}
              maxLength={BLOG_LIMITS.seoTitle}
              placeholder={draft.title}
              onChange={(event) => set('seoTitle', event.target.value)}
            />
            {draft.seoTitle ? (
              <small className="dash-page-settings-hint">
                {t('pageSettings.seoChars', { count: draft.seoTitle.length, max: SEO_TITLE_CHARS })}
              </small>
            ) : null}
          </label>
          <label className="dash-field">
            <span>{t('pageSettings.seoDesc')} <InfoHint text={t('pageSettings.seoDescHint')} /></span>
            <textarea
              rows={3}
              className="dash-placeholder-strong"
              value={draft.seoDescription}
              maxLength={BLOG_LIMITS.seoDescription}
              placeholder={draft.excerpt}
              onChange={(event) => set('seoDescription', event.target.value)}
            />
            {draft.seoDescription ? (
              <small className="dash-page-settings-hint">
                {t('pageSettings.seoChars', { count: draft.seoDescription.length, max: SEO_DESCRIPTION_CHARS })}
              </small>
            ) : null}
          </label>
        </section>

        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
              <IconShare size={18} />
            </span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('blog.seo.shareTitle')}</h3>
              <p>{t('blog.seo.shareBody')}</p>
            </div>
          </div>
          {/* La tarjeta que sale al pegar el enlace, con la imagen pulsable: la
              misma pieza que la página de una oferta. */}
          <div className="dash-page-settings-og">
            <button
              type="button"
              className={`dash-page-settings-og-image${shareImage ? ' has-image' : ''}`}
              aria-label={t('blog.seo.pick')}
              onClick={() => setPicking('seo')}
            >
              {shareImage ? (
                <>
                  <img src={shareImage} alt="" />
                  <span className="dash-record-hero-cover-mark" aria-hidden="true">
                    <IconPhotoPlus size={18} />
                    <span>{t('common.changeImage')}</span>
                  </span>
                </>
              ) : <IconPhoto size={24} />}
            </button>
            <div className="dash-page-settings-og-copy">
              <small>/{basePath}/{draft.slug || blogSlug(draft.title) || '…'}</small>
              <strong>{seoTitle}</strong>
              {seoDescription ? <p>{seoDescription}</p> : null}
            </div>
          </div>
          <p className="dash-page-settings-hint">
            {t(draft.seoImage ? 'blog.seo.customImage' : 'blog.seo.imageDefault')}
            {draft.seoImage ? (
              <button type="button" className="dash-ofd-page-inline-btn" onClick={() => set('seoImage', '')}>
                {t('blog.seo.useCover')}
              </button>
            ) : null}
          </p>
        </section>
      </div>
      </div>

      <aside className="dash-blog-aside">
        {/* La entrada de un vistazo, en cualquier pestaña: portada, nombre,
            estado, los datos que se consultan de pasada y los mandos —el enlace
            y publicar—. Es la tarjeta de la ficha de una oferta (`dash-record-
            hero is-vertical`), no una parecida. */}
        <section className="dash-record-hero is-vertical" aria-label={draft.title || t('blog.untitled')}>
          <div className="dash-blog-cover-frame">
            <button
              type="button"
              className={`dash-record-hero-cover${draft.coverImage ? ' has-image' : ' is-empty'}`}
              aria-label={t(draft.coverImage ? 'blog.editor.changeCover' : 'blog.editor.addCover')}
              onClick={() => setPicking('cover')}
            >
              {draft.coverImage ? (
                <img src={draft.coverImage} alt="" />
              ) : (
                <span className="dash-record-hero-cover-mark is-empty" aria-hidden="true">
                  <IconPhotoPlus size={28} stroke={1.6} />
                  <span>{t('blog.editor.addCover')}</span>
                </span>
              )}
            </button>
            {draft.coverImage ? (
              <div className="dash-blog-cover-actions">
                <button type="button" onClick={() => setPicking('cover')}>
                  <IconPhoto size={16} aria-hidden="true" /> {t('common.change')}
                </button>
                <button type="button" aria-label={t('common.remove')} title={t('common.remove')} onClick={() => set('coverImage', '')}>
                  <IconTrash size={16} aria-hidden="true" />
                </button>
              </div>
            ) : null}
          </div>

          <div className="dash-record-hero-copy">
            <span className="dash-record-hero-kind">{t('blog.editor.kind')}</span>
            <div className="dash-record-hero-titleline">
              <h2 title={draft.title}>{draft.title.trim() || t('blog.untitled')}</h2>
              <span className={`dash-offer-status ${state === 'published' ? 'is-active' : state === 'scheduled' ? 'is-review' : 'is-draft'}`}>
                {t(`blog.state.${state}`)}
              </span>
            </div>
            {draft.excerpt.trim() ? <p title={draft.excerpt}>{draft.excerpt.trim()}</p> : null}
            {draft.authorName.trim() || draft.categories.length ? (
              <ul className="dash-record-hero-meta">
                {draft.authorName.trim() ? <li>{draft.authorName.trim()}</li> : null}
                {draft.categories.length ? <li>{draft.categories.join(', ')}</li> : null}
              </ul>
            ) : null}
          </div>

          {publishedAt ? (
            <time className="dash-blog-publication-date" dateTime={publishedAt}>{publicationLabel}</time>
          ) : null}

          {/* Una entrada publicada debe volver a borrador para cambiar su fecha.
              Los borradores y las programadas conservan el selector. */}
          {state !== 'published' ? (
            <div className="dash-blog-field dash-blog-hero-date">
              {/* «Quitar» junto al rótulo y no como tercer mando en la fila: al
                  lado de la fecha y la hora no cabía sin partir la fecha en dos. */}
              <span className="dash-blog-panel-labelrow">
                <span className="dash-blog-panel-label">{t('blog.field.date')}</span>
                {draft.publishedAt ? (
                  <button type="button" className="dash-blog-date-clear" onClick={() => set('publishedAt', '')}>
                    {t('blog.field.dateClear')}
                  </button>
              ) : null}
            </span>
            <div className={`dash-blog-date${draft.publishedAt ? ' has-time' : ''}`}>
              <SingleDatePicker
                value={draft.publishedAt.slice(0, 10)}
                placeholder={t('blog.field.dateNow')}
                label={t('blog.field.date')}
                onChange={(day) => set('publishedAt', `${day}T${draft.publishedAt.slice(11, 16) || '09:00'}`)}
              />
              {draft.publishedAt ? (
                <input
                  type="time"
                  className="dash-blog-time"
                  aria-label={t('blog.field.time')}
                  value={draft.publishedAt.slice(11, 16)}
                  onChange={(event) => { if (event.target.value) set('publishedAt', `${draft.publishedAt.slice(0, 10)}T${event.target.value}`) }}
                />
              ) : null}
            </div>
            {futureDate ? <small>{t('blog.field.dateFuture')}</small> : null}
          </div>
          ) : null}

          <div className="dash-record-hero-actions">
            {publicUrl ? (
              <div className="dash-blog-link-actions">
                <button type="button" className="dash-secondary-btn" aria-label={t(copied ? 'blog.link.copied' : 'blog.link.copy')} onClick={() => { void copyLink() }}>
                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  <span>{t(copied ? 'blog.link.copied' : 'blog.link.copy')}</span>
                </button>
                {/* Abrir sólo publicada: un borrador en el sitio contesta «no existe». */}
                {post?.state === 'published' ? (
                  <a href={publicUrl} target="_blank" rel="noreferrer" className="dash-secondary-btn" aria-label={t('blog.link.open')}>
                    <IconExternalLink size={14} />
                    <span>{t('blog.link.open')}</span>
                  </a>
                ) : null}
              </div>
            ) : siteOrigin ? null : (
              <small className="dash-ofd-need-site">{t('blog.link.needSite')}</small>
            )}
            {liveHint ? <small className="dash-ofd-need-site">{liveHint}</small> : null}
            {post?.status !== 'published' ? (
              <button type="button" className="dash-primary-btn" disabled={saving || (!draft.title.trim() && !post)} onClick={() => { void save('published') }}>
                <IconSend size={15} /> {t(futureDate ? 'blog.editor.schedule' : 'blog.editor.publish')}
              </button>
            ) : null}
          </div>
        </section>

        {tab === 'write' && draft.coverImage ? (
          <div className="dash-blog-panel">
            <label className="dash-blog-field">
              <span className="dash-blog-panel-label"><IconPhoto size={14} aria-hidden="true" />{t('blog.field.coverAlt')}</span>
              <input value={draft.coverAlt} maxLength={300} onChange={(event) => set('coverAlt', event.target.value)} />
            </label>
          </div>
        ) : null}
        {post ? (
          <section className="dash-blog-panel dash-blog-post-actions" aria-label={t('blog.col.actions')}>
            <h3 className="dash-blog-panel-label">{t('blog.col.actions')}</h3>
            {post.status === 'published' ? (
              <button type="button" className="dash-secondary-btn" disabled={saving} onClick={() => { void save('draft') }}>
                <IconFileText size={15} aria-hidden="true" /> {t('blog.editor.unpublish')}
              </button>
            ) : null}
            <button type="button" className="dash-secondary-btn dash-blog-delete" disabled={saving} onClick={() => setConfirmDelete(true)}>
              <IconTrash size={15} aria-hidden="true" /> {t('blog.editor.delete')}
            </button>
          </section>
        ) : null}
      </aside>
      </div>


      {picking ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => { set(picking === 'cover' ? 'coverImage' : 'seoImage', url); setPicking(null) }}
          onClose={() => setPicking(null)}
        />
      ) : null}

      {confirmDelete && post ? (
        <ConfirmDialog
          title={t('blog.delete.title', { title: post.title || t('blog.untitled') })}
          message={t('blog.delete.body')}
          confirmLabel={t('blog.delete.confirm')}
          tone="danger"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            try {
              await deleteBlogPost(projectId, post.id, token)
              showToast(t('blog.toast.deleted'))
              setConfirmDelete(false)
              /* Borrada no hay nada que guardar: sin limpiar esto, la barra
                 de pendientes impediría volver a la lista. */
              setDraft(base)
              setTyping(false)
              onDeleted()
            } catch (error) {
              showToast(errorMessage(error), 'error')
              throw error
            }
          }}
        />
      ) : null}
    </div>
  )
}
